import { cn, ResizablePanelGroup, ResizablePanel, ResizableHandle, type ImperativePanelGroupHandle } from '@duck/ui'
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { WorkspaceTabsView, WorkspaceTabsViewRef } from './WorkspaceTabsView'
import { ChallengeService } from '@duck/api-definitions'
import { apis } from '@modules/apiClients'
import { debounce } from 'lodash'
import DOMPurify from 'dompurify'
import { useSetAtom, useAtomValue, useStore } from 'jotai'
import { lastSaveWorkDateAtom, isSavingWorkAtom, currentSolutionAtom } from '@/store/saveWorkAtoms'
import { workspaceTabsHasTwoViewsAtom, workspaceSolutionHandlersAtom } from '@/store/workspaceAtoms'
import { useEventEmitter, useEventListener } from '@/modules/EventBusModule'
import { getChartContainerHtml, getSolutionBlockWithDeleteButtonHtml } from '@/components/WysiwygEditor/wysiwygUtils'

type ViewTabState = ChallengeService.WorkspaceTab

type ViewState = {
    tabs: ViewTabState[]
    activeTabIndex: number
}

const hasSolutionTabInViews = (views: ViewState[]): boolean =>
    views.some((v) => v.tabs.some((t) => t.type === ChallengeService.WorkspaceTabTypeEnum.Solution))

type TabViews = NonNullable<ChallengeService.WorkState['tabViews']>

// Convert contract tabViews to component view state
const contractTabViewsToViews = (tabViews?: ChallengeService.WorkState['tabViews'], defaultQuery?: string): ViewState[] => {
    if (!tabViews || tabViews.length === 0) {
        // Default state if no tabs saved
        return [
            {
                tabs: [
                    { type: ChallengeService.WorkspaceTabTypeEnum.Assignment },
                    { type: ChallengeService.WorkspaceTabTypeEnum.Sql, data: defaultQuery },
                    { type: ChallengeService.WorkspaceTabTypeEnum.Solution },
                ],
                activeTabIndex: 0,
            }
        ]
    }
    return tabViews.map((tabView) => {
        const tabs = tabView.tabs.map((tab) => ({
            type: tab.type,
            data: tab.data,
        }))
        // Persisted activeTab may be out of range (e.g. after tabs were removed); clamp to valid index to avoid downstream indexing errors.
        const activeTab = tabView.activeTab
        const activeTabIndex =
            tabs.length === 0 ? 0 : Math.min(Math.max(0, activeTab), tabs.length - 1)
        return { tabs, activeTabIndex }
    })
}

// Convert component view state to contract tabViews
const viewsToContractTabViews = (views: ViewState[], widths?: number[]): TabViews => {
    return views.map((view, index): ChallengeService.WorkspaceTabView => ({
        activeTab: view.activeTabIndex,
        tabs: view.tabs.map(
            (tab): ChallengeService.WorkspaceTab => ({
                type: tab.type,
                data: tab.data,
            })
        ),
        width: widths?.[index],
    }))
}

export interface WorkspaceTabsProps {
    challenge: ChallengeService.ChallengeInfo['challenge']
    challengeId: string
    defaultQuery?: string
    className?: string
}

const MAX_VIEWS = 2

export const WorkspaceTabs = ({
    challenge,
    challengeId,
    defaultQuery = '',
    className,
}: WorkspaceTabsProps) => {
    const setLastSaveDate = useSetAtom(lastSaveWorkDateAtom)
    const setIsSaving = useSetAtom(isSavingWorkAtom)
    const setHasTwoViews = useSetAtom(workspaceTabsHasTwoViewsAtom)

    // Initialize from contract data
    const { initialViews, initialWidths } = useMemo(() => {
        const tabViews = challenge.workState?.tabViews
        const views = contractTabViewsToViews(tabViews, defaultQuery)
        const widths = tabViews?.map((tv) => (typeof tv.width === 'number' ? tv.width : 50)) ?? []

        return { initialViews: views, initialWidths: widths }
    }, [challenge.workState, defaultQuery])

    const [views, setViews] = useState<ViewState[]>(initialViews)
    const [panelWidths, setPanelWidths] = useState<number[]>(initialWidths)
    const viewsRef = useRef<ViewState[]>(initialViews)
    const secondViewRef = useRef<WorkspaceTabsViewRef>(null)
    const panelGroupRef = useRef<ImperativePanelGroupHandle>(null)

    /** Key for WorkspaceTabsView: changes only when view structure changes (tabs added/removed/reordered). Typing changes tab.data but not structure, so no remount. */
    const viewKeys = useMemo(
        () =>
            views.map((v, i) => `${i}-${v.tabs.length}-${v.tabs.map((t) => t.type).join(',')}`),
        [views]
    )

    const solutionHandlers = useAtomValue(workspaceSolutionHandlersAtom)
    const store = useStore()
    const setCurrentSolution = useSetAtom(currentSolutionAtom)
    const solutionRef = useRef<string>('')
    useEffect(() => {
        solutionRef.current = store.get(currentSolutionAtom)
        const unsub = store.sub(currentSolutionAtom, () => {
            solutionRef.current = store.get(currentSolutionAtom)
        })
        return unsub
    }, [store])

    // When no Solution tab is open, append insert/chart to workState so it persists
    useEventListener(
        'solution:insertHtml',
        ({ value: html, deleteButton }) => {
            if (hasSolutionTabInViews(views)) return
            const sanitized = DOMPurify.sanitize(html)
            const toAppend = deleteButton
                ? getSolutionBlockWithDeleteButtonHtml(sanitized)
                : sanitized + '<div>&nbsp;</div>'
            setCurrentSolution((prev) => {
                const next = prev + toAppend
                solutionHandlers?.handleSolutionChange(next)
                return next
            })
        },
        [views, solutionHandlers, setCurrentSolution]
    )
    useEventListener(
        'chart:added',
        ({ chart }) => {
            if (hasSolutionTabInViews(views) || !chart?.id) return
            const newSolution = solutionRef.current + getChartContainerHtml(chart)
            setCurrentSolution(newSolution)
            solutionHandlers?.handleChartAdded(newSolution, chart)
        },
        [views, solutionHandlers, setCurrentSolution]
    )

    // Set initial two views state
    useEffect(() => {
        setHasTwoViews(initialViews.length === 2)
    }, []) // Only run on mount

    // Update views when challenge data changes
    useEffect(() => {
        const tabViews = challenge.workState?.tabViews
        const newViews = contractTabViewsToViews(tabViews, defaultQuery)
        const newWidths = tabViews?.map((tv) => (typeof tv.width === 'number' ? tv.width : 50)) ?? []
        setViews(newViews)
        viewsRef.current = newViews
        setPanelWidths(newWidths)
        setHasTwoViews(newViews.length === 2)
    }, [challenge.workState, setHasTwoViews, defaultQuery])

    // Debounced save function
    const saveWorkspaceTabsState = useMemo(
        () => debounce(async (viewsToSave: ViewState[], widths?: number[]) => {
            if (!challengeId) return
            setIsSaving(true)
            try {
                const tabViews = viewsToContractTabViews(viewsToSave, widths)
                await apis.challengeService.saveWork({
                    challengeId,
                    workState: {
                        tabViews,
                    },
                })
                setLastSaveDate(new Date())
            } catch (err) {
                console.error('Failed to save tabs state:', err)
            } finally {
                setIsSaving(false)
            }
        }, 500),
        [challengeId, setIsSaving, setLastSaveDate]
    )

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            saveWorkspaceTabsState.cancel()
        }
    }, [saveWorkspaceTabsState])

    const handleResetSplit = useCallback(() => {
        panelGroupRef.current?.setLayout([50, 50])
    }, [])

    const sqlTabInitialData = defaultQuery

    /** Move existing tab: remove from source view and open in new view (1 view) or add to other view (2 views). */
    const handleMoveTabToSplitView = useCallback(
        (tab: ChallengeService.WorkspaceTab, sourceViewIndex: number, tabIndexInView: number) => {
            const currentViews = views
            const source = currentViews[sourceViewIndex]
            const newTabsSource = source.tabs.filter((_, i) => i !== tabIndexInView)
            const newActiveSource =
                newTabsSource.length === 0
                    ? 0
                    : Math.min(
                        source.activeTabIndex >= tabIndexInView ? Math.max(0, source.activeTabIndex - 1) : source.activeTabIndex,
                        newTabsSource.length - 1
                    )
            const newViewSource: ViewState = { tabs: newTabsSource, activeTabIndex: newActiveSource }
            const sourceHasNoMoreItems = newTabsSource.length === 0

            if (currentViews.length === 1) {
                if (sourceHasNoMoreItems) {
                    // Only one tab existed; move becomes the single view (no split)
                    const singleView: ViewState = { tabs: [tab], activeTabIndex: 0 }
                    setViews([singleView])
                    setPanelWidths([50])
                    setHasTwoViews(false)
                    viewsRef.current = [singleView]
                    saveWorkspaceTabsState([singleView], [50])
                } else {
                    const newViewWithTab: ViewState = { tabs: [tab], activeTabIndex: 0 }
                    const newViews = [newViewSource, newViewWithTab]
                    const newWidths = [panelWidths[0] ?? 50, 50]
                    setViews(newViews)
                    viewsRef.current = newViews
                    setPanelWidths(newWidths)
                    setHasTwoViews(true)
                    saveWorkspaceTabsState(newViews, newWidths)
                }
                return
            }

            const otherIndex = 1 - sourceViewIndex
            const otherView = currentViews[otherIndex]
            const newTabsOther: ChallengeService.WorkspaceTab[] = [...otherView.tabs, tab]
            const newViewOther: ViewState = { tabs: newTabsOther, activeTabIndex: newTabsOther.length - 1 }

            if (sourceHasNoMoreItems) {
                // Close the now-empty source view; keep only the other view
                const singleView = newViewOther
                setViews([singleView])
                viewsRef.current = [singleView]
                setPanelWidths([50])
                setHasTwoViews(false)
                saveWorkspaceTabsState([singleView], [50])
            } else {
                const newViews = [...currentViews]
                newViews[sourceViewIndex] = newViewSource
                newViews[otherIndex] = newViewOther
                setViews(newViews)
                viewsRef.current = newViews
                saveWorkspaceTabsState(newViews, panelWidths)
            }
        },
        [views, saveWorkspaceTabsState, panelWidths, setHasTwoViews]
    )

    /** Add a new tab in a second view (only when 1 view; used from + menu). */
    const handleAddTabInSplitView = useCallback(
        (tabType: ChallengeService.WorkspaceTabTypeEnum) => {
            if (views.length >= MAX_VIEWS) return
            const newView: ViewState = {
                tabs: [
                    {
                        type: tabType,
                        data: tabType === ChallengeService.WorkspaceTabTypeEnum.Sql ? sqlTabInitialData : undefined,
                    },
                ],
                activeTabIndex: 0,
            }
            const newViews: ViewState[] = [...views, newView]
            const newWidths = [...panelWidths, 50]
            setViews(newViews)
            viewsRef.current = newViews
            setPanelWidths(newWidths)
            setHasTwoViews(true)
            saveWorkspaceTabsState(newViews, newWidths)
        },
        [views, saveWorkspaceTabsState, panelWidths, setHasTwoViews, sqlTabInitialData]
    )

    /** Add a new tab in the other view (when 2 views; used from + menu). */
    const handleAddTabInOtherView = useCallback(
        (sourceViewIndex: number) => (tabType: ChallengeService.WorkspaceTabTypeEnum) => {
            if (views.length !== 2) return
            const otherIndex = 1 - sourceViewIndex
            const otherView = views[otherIndex]
            const newTab: ViewTabState = {
                type: tabType,
                data: tabType === ChallengeService.WorkspaceTabTypeEnum.Sql ? sqlTabInitialData : undefined,
            }
            const newTabsOther = [...otherView.tabs, newTab]
            const newViewOther: ViewState = { tabs: newTabsOther, activeTabIndex: newTabsOther.length - 1 }
            const newViews = [...views]
            newViews[otherIndex] = newViewOther
            setViews(newViews)
            viewsRef.current = newViews
            saveWorkspaceTabsState(newViews, panelWidths)
        },
        [views, saveWorkspaceTabsState, panelWidths, sqlTabInitialData]
    )

    const handleCloseSecondView = useCallback(() => {
        const newViews: ViewState[] = [views[0]]
        setPanelWidths([50])
        setViews(newViews)
        viewsRef.current = newViews
        setHasTwoViews(false)
        saveWorkspaceTabsState(newViews, [50])
    }, [views, saveWorkspaceTabsState, setHasTwoViews])

    /** Structural change: tabs reordered, added, removed, or active tab switched. Updates state and persists. */
    const handleTabsChange = useCallback(
        (viewIndex: number) =>
            (tabs: ChallengeService.WorkspaceTab[], activeTabIndex: number) => {
                setViews((currentViews) => {
                    const next = [...currentViews]
                    if (viewIndex < next.length) {
                        next[viewIndex] = { tabs, activeTabIndex }
                    }
                    viewsRef.current = next
                    saveWorkspaceTabsState(next, panelWidths)
                    return next
                })
            },
        [saveWorkspaceTabsState, panelWidths]
    )

    /** Content-only change: tab data updated (e.g. SQL typing). Updates ref and persists, no rerender. */
    const handleTabsDataChange = useCallback(
        (viewIndex: number) =>
            (tabs: ChallengeService.WorkspaceTab[], activeTabIndex: number) => {
                const current = viewsRef.current
                if (viewIndex >= current.length) return
                const next = [...current]
                next[viewIndex] = { tabs, activeTabIndex }
                viewsRef.current = next
                saveWorkspaceTabsState(next, panelWidths)
            },
        [saveWorkspaceTabsState, panelWidths]
    )

    const emitChartsTriggerRedraw = useEventEmitter('charts:triggerRedraw', 10)

    const handleLayoutChange = useCallback(
        (sizes: number[]) => {
            setPanelWidths(sizes)
            saveWorkspaceTabsState(viewsRef.current, sizes)
            emitChartsTriggerRedraw({})
        },
        [saveWorkspaceTabsState, emitChartsTriggerRedraw]
    )

    if (views.length === 1) {
        return (
            <div className={cn('w-full min-h-0 flex-1 flex', className)}>
                <WorkspaceTabsView
                    key={viewKeys[0]}
                    ref={undefined}
                    initialTabsState={views[0]}
                    defaultQuery={defaultQuery}
                    onMoveTabToSplitView={(tab, tabIndex) => handleMoveTabToSplitView(tab, 0, tabIndex)}
                    splitViewTooltipKey="common.moveToSplitView"
                    onAddTabInSplitView={handleAddTabInSplitView}
                    addTabInSplitViewTooltipKey="common.openInSplitView"
                    onCloseView={undefined}
                    onTabsChange={handleTabsChange(0)}
                    onTabsDataChange={handleTabsDataChange(0)}
                />
            </div>
        )
    }

    return (
        <ResizablePanelGroup
            ref={panelGroupRef}
            direction="horizontal"
            className={cn('w-full h-full', className)}
            onLayout={handleLayoutChange}
        >
            <ResizablePanel
                key={0}
                defaultSize={panelWidths[0] || 50}
                minSize={20}
                maxSize={80}
                order={0}
                className="min-w-0 min-h-0 flex flex-col "
            >
                <WorkspaceTabsView
                    key={viewKeys[0]}
                    ref={undefined}
                    initialTabsState={views[0]}
                    defaultQuery={defaultQuery}
                    onMoveTabToSplitView={(tab, tabIndex) => handleMoveTabToSplitView(tab, 0, tabIndex)}
                    splitViewTooltipKey="common.moveToOtherView"
                    onAddTabInSplitView={handleAddTabInOtherView(0)}
                    addTabInSplitViewTooltipKey="common.openInOtherView"
                    onCloseView={undefined}
                    onTabsChange={handleTabsChange(0)}
                    onTabsDataChange={handleTabsDataChange(0)}
                />
            </ResizablePanel>
            <ResizableHandle className="shrink-0 cursor-col-resize mt-[52px]" onClick={handleResetSplit} />
            <ResizablePanel
                key={1}
                defaultSize={panelWidths[1] || 50}
                minSize={20}
                maxSize={80}
                order={1}
                className="min-w-0 min-h-0 flex flex-col"
            >
                <WorkspaceTabsView
                    key={viewKeys[1]}
                    ref={secondViewRef}
                    initialTabsState={views[1]}
                    defaultQuery={defaultQuery}
                    onMoveTabToSplitView={(tab, tabIndex) => handleMoveTabToSplitView(tab, 1, tabIndex)}
                    splitViewTooltipKey="common.moveToOtherView"
                    onAddTabInSplitView={handleAddTabInOtherView(1)}
                    addTabInSplitViewTooltipKey="common.openInOtherView"
                    onCloseView={handleCloseSecondView}
                    onTabsChange={handleTabsChange(1)}
                    onTabsDataChange={handleTabsDataChange(1)}
                />
            </ResizablePanel>
        </ResizablePanelGroup>
    )
}

